import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { Card, Badge, Avatar, Spinner, Alert } from '@opendate-app/setlist';
import crmLabels from '../../shared/utils/crmLabels';

const qs = require('qs');

import {
  openStagesFor,
  getStageBadgeConfig,
  getStageBadgeColor,
  getTypeLabel,
  getTypeBadgeColor,
  formatAmount,
  formatCloseDate,
  initialsFor,
} from './dealStages';

function DealCard({ team, deal, types, dragging }) {
  return (
    <Card
      padding="md"
      style={{ cursor: 'grab', opacity: dragging ? 0.4 : 1 }}
    >
      <div className="d-flex align-items-start" style={{ gap: 10 }}>
        <Avatar size="sm" initials={initialsFor(deal.name)} alt={deal.name} />

        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="d-flex justify-content-between align-items-start" style={{ gap: 8 }}>
            <div className="figma-text sm semibold" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <a
                href={`/teams/${team.id}/crm/deals/${deal.id}`}
                className="text-reset text-decoration-none"
                onPointerDown={(e) => e.stopPropagation()}
              >
                {deal.name}
              </a>
            </div>
            <span className="figma-text sm semibold" style={{ whiteSpace: 'nowrap' }}>
              {formatAmount(deal.amount)}
            </span>
          </div>

          {deal.organization_name && (
            <div className="figma-text tertiary xs" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {deal.organization_name}
            </div>
          )}

          <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 8, gap: 8 }}>
            {deal.deal_type ? (
              <Badge size="sm" color={getTypeBadgeColor(deal.deal_type)} dot>
                {getTypeLabel(deal.deal_type, types)}
              </Badge>
            ) : (
              <span />
            )}
            <span className="figma-text quaternary xs">{formatCloseDate(deal.close_date)}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

function DraggableDealCard(props) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: props.deal.id,
    data: { stage: props.deal.stage },
  });

  return (
    <div ref={setNodeRef} {...attributes} {...listeners} style={{ marginBottom: 10 }}>
      <DealCard {...props} dragging={isDragging} />
    </div>
  );
}

function PipelineColumn({ team, stage, deals, types }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const { label } = getStageBadgeConfig(stage);
  const labels = crmLabels(team);
  const columnTotal = deals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);

  return (
    <div
      style={{
        flex: '0 0 300px',
        width: 300,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ padding: '0 4px 8px' }}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <Badge size="sm" color={getStageBadgeColor(stage)} dot>{label}</Badge>
            <span className="figma-text tertiary sm">{deals.length}</span>
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-end" style={{ marginTop: 2 }}>
          <span className="figma-text tertiary xs semibold">{formatAmount(columnTotal)}</span>
        </div>
      </div>

      <div
        ref={setNodeRef}
        style={{
          flex: 1,
          minHeight: 120,
          borderRadius: 10,
          padding: 8,
          background: isOver ? 'var(--sl-bg-secondary)' : 'var(--sl-bg-tertiary)',
          border: `1px dashed ${isOver ? 'var(--sl-border-primary)' : 'transparent'}`,
          transition: 'background 0.12s ease',
        }}
      >
        {deals.map((deal) => (
          <DraggableDealCard key={deal.id} team={team} deal={deal} types={types} />
        ))}
        {deals.length === 0 && (
          <div className="figma-text quaternary xs text-center" style={{ padding: '16px 0' }}>
            {`No ${labels.deal.plural.toLowerCase()}`}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, hint }) {
  return (
    <Card padding="md" style={{ flex: 1, minWidth: 160 }}>
      <div className="figma-text tertiary xs semibold" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div className="figma-text xl semibold" style={{ marginTop: 6 }}>{value}</div>
      {hint && <div className="figma-text quaternary xs" style={{ marginTop: 2 }}>{hint}</div>}
    </Card>
  );
}

function DealsPipelineBoard({ team, csrfToken, stages = {}, category = 'sales', types = [], searchTerm = '', refreshKey = 0, onCountChange }) {
  const columns = stages[category] || [];
  const labels = crmLabels(team);
  const [dealsByStage, setDealsByStage] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeDeal, setActiveDeal] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const requestRef = useRef(0);

  const fetchBoard = useCallback(async () => {
    if (!team || !team.id) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    try {
      setLoading(true);
      setError(null);
      if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const q = { deal_type_eq: category };
      if (searchTerm) q.name_cont = searchTerm;

      const response = await axios.get(`/teams/${team.id}/crm/deals.json`, {
        params: { view: 'board', q },
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets' }),
      });
      if (requestId !== requestRef.current) return;

      const byStage = response.data.deals_by_stage || {};
      setDealsByStage(byStage);
      if (onCountChange) {
        onCountChange(Object.values(byStage).reduce((sum, list) => sum + list.length, 0));
      }
    } catch (e) {
      if (requestId !== requestRef.current) return;
      setError('Failed to load pipeline');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [team && team.id, csrfToken, searchTerm, category, onCountChange]);

  const fetchBoardRef = useRef(fetchBoard);
  useEffect(() => {
    fetchBoardRef.current = fetchBoard;
  }, [fetchBoard]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard, refreshKey]);

  const summary = useMemo(() => {
    const openDeals = openStagesFor(category).flatMap((stage) => dealsByStage[stage] || []);
    const openCount = openDeals.length;
    const pipelineValue = openDeals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
    const avgAsk = openCount === 0 ? 0 : Math.round(pipelineValue / openCount);
    return { openCount, pipelineValue, avgAsk };
  }, [dealsByStage, category]);

  const findDeal = (id) => {
    for (const stage of Object.keys(dealsByStage)) {
      const match = (dealsByStage[stage] || []).find((d) => d.id === id);
      if (match) return { deal: match, stage };
    }
    return null;
  };

  const handleDragStart = ({ active }) => {
    const found = findDeal(active.id);
    setActiveDeal(found ? found.deal : null);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveDeal(null);
    if (!over) return;

    const found = findDeal(active.id);
    if (!found) return;

    const fromStage = found.stage;
    const toStage = over.id;
    if (fromStage === toStage) return;

    const original = found.deal;
    const moved = { ...original, stage: toStage };

    setDealsByStage((prev) => ({
      ...prev,
      [fromStage]: (prev[fromStage] || []).filter((d) => d.id !== moved.id),
      [toStage]: [moved, ...(prev[toStage] || [])],
    }));

    try {
      if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.patch(`/teams/${team.id}/crm/deals/${moved.id}.json`, { deal: { stage: toStage } });
    } catch (e) {
      toast.error(`Failed to move ${labels.deal.singular.toLowerCase()}`);
      fetchBoardRef.current();
    }
  };

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert color="error" title="Failed to load pipeline" description={error} />
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex flex-wrap" style={{ gap: 12, marginBottom: 16 }}>
        <KpiCard label="Open opportunities" value={summary.openCount.toLocaleString()} hint="In open stages" />
        <KpiCard label="Pipeline value" value={formatAmount(summary.pipelineValue)} hint="Sum of open amounts" />
        <KpiCard label={`Avg ${labels.deal.singular.toLowerCase()} size`} value={formatAmount(summary.avgAsk)} hint="Per open opportunity" />
      </div>

      {loading ? (
        <div className="d-flex justify-content-center" style={{ padding: 24 }}>
          <Spinner label="Loading pipeline…" />
        </div>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="d-flex" style={{ gap: 12, overflowX: 'auto', paddingBottom: 8, alignItems: 'stretch' }}>
            {columns.map((stage) => (
              <PipelineColumn
                key={stage}
                team={team}
                stage={stage}
                deals={dealsByStage[stage] || []}
                types={types}
              />
            ))}
          </div>

          <DragOverlay>
            {activeDeal ? (
              <div style={{ width: 284 }}>
                <DealCard team={team} deal={activeDeal} types={types} dragging={false} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

export default DealsPipelineBoard;
